import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import React from 'react';
import classNames from 'classnames';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Badge from 'components/partials/badge/badge';
import IconButton from 'components/partials/button/icon-button';
import { TextArea } from 'ui/text-area';

import { INPUT_MAX_HEIGHT } from '../../ai-assistant.constants';

import Icons from 'assets/icons';

export interface MessageInputHandle {
  focus: () => void;
}

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  customClass?: string;
  /** Pre-fills the textarea, e.g. with the content of the message being edited. */
  initialValue?: string;
  /** Renders an "Edit" banner above the input with a close action; see `onCancelEditing`. */
  isEditing?: boolean;
  onCancelEditing?: () => void;
}

const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  (
    {
      onSendMessage,
      isLoading = false,
      disabled = false,
      placeholder = 'Ask something',
      rows = 1,
      maxLength = 10000,
      customClass = '',
      initialValue = '',
      isEditing = false,
      onCancelEditing,
    },
    ref
  ): React.JSX.Element => {
    const [message, setMessage] = useState(initialValue);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const isDisabled = isLoading || disabled;

    const trimmedMessage = message.trim();
    const isSubmitDisabled = !trimmedMessage || isDisabled;

    const resetTextareaHeight = (): void => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    };

    const handleSubmit = (
      e: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>
    ): void => {
      e.preventDefault();

      if (isSubmitDisabled) return;

      onSendMessage(trimmedMessage);
      setMessage('');
      resetTextareaHeight();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(
          textareaRef.current.scrollHeight,
          INPUT_MAX_HEIGHT
        )}px`;
      }
    }, [message]);

    return (
      <form
        onSubmit={handleSubmit}
        className={classNames('w-full', customClass)}
      >
        <Box
          sx={[
            { position: 'relative' },
            !!isEditing && {
              borderRadius: '0.5rem',
              borderWidth: '1px',
              borderColor: 'tokens.navy.500',
              overflow: 'hidden',
            },
          ]}
        >
          {isEditing && (
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'tokens.gray.100',
                pl: 2,
                pr: 1,
                py: 0.5,
              }}
            >
              <Badge
                text="Edit"
                variant="neutral"
                icon={<Icons.EditPencil customClass="w-3 h-3" />}
              />
              <IconButton
                variant="reserve-text"
                size="sm"
                icon={<Icons.Close />}
                onClick={onCancelEditing}
                aria-label="Cancel editing"
                data-testid="message-input-cancel-edit-button"
              />
            </Stack>
          )}
          <TextArea
            ref={textareaRef}
            aria-label="Message"
            size="medium"
            resize="none"
            value={message}
            onChange={setMessage}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isDisabled}
            rows={rows}
            showCharCount={false}
            maxLength={maxLength}
            controlSx={[
              { '& .MuiOutlinedInput-input': { py: 1.5, pl: 2, pr: 6 } },
              isEditing && {
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-disabled .MuiOutlinedInput-notchedOutline, &.Mui-disabled:hover .MuiOutlinedInput-notchedOutline':
                  { borderColor: 'transparent' },
                '&.Mui-focused': { boxShadow: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: 'secondary.main',
                },
              },
            ]}
          />
          <button
            type="submit"
            disabled={isSubmitDisabled}
            aria-label="Send message"
            className="absolute right-3 bottom-2 flex items-center justify-center p-2 rounded-lg disabled:cursor-default enabled:hover:bg-navy-100"
          >
            {isLoading ? (
              <Icons.Spinner customClass="w-4 h-4 animate-spin" />
            ) : (
              <Icons.ArrowUpIndicator
                customClass={classNames('transform rotate-[-45deg]', {
                  'text-navy-500': isSubmitDisabled,
                  'text-navy-800': !isSubmitDisabled,
                })}
              />
            )}
          </button>
        </Box>
      </form>
    );
  }
);

MessageInput.displayName = 'MessageInput';

export default MessageInput;
